/**
 * CampaignHistoryTable — Custom Invite panel's campaign history list (plan
 * `2026-09-24-005-plays-tab-one-pr-plan.md`, U3/Item E).
 *
 * Renders `lookup.data.campaigns` (U3a's wire shape, `src/shared/flow/
 * custom-invite.ts`'s `CustomInviteCampaignHistoryRowSchema`) as a Glaze
 * `Table`: Campaign / Start date / Status. The row whose `campaign_id`
 * matches the currently-selected campaign (if any) is tinted so a rep can
 * see "this is the campaign I just picked" among the history.
 *
 * Status comes from `campaignStatusBadge` (U9,
 * `mini-apps/_shared/campaign-status-badge.ts`) — imported via a relative
 * path per plan OQ4 (web may import `mini-apps/_shared/**` only; this file
 * does not create its own badge-mapping module).
 */
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@clay-run/glaze/table';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';
import type { CustomInviteCampaignHistoryRow } from '@shared/flow/custom-invite';
import { campaignStatusBadge } from '../../../../../../mini-apps/_shared/campaign-status-badge';

export interface CampaignHistoryTableProps {
  rows: CustomInviteCampaignHistoryRow[];
  /** The currently-selected campaign's `play_instance_ref`/`campaign_id`, or null when no campaign is picked yet. */
  selectedCampaignId: string | null;
}

export function CampaignHistoryTable({ rows, selectedCampaignId }: CampaignHistoryTableProps) {
  return (
    <div className="space-y-1.5">
      <Text size="xs" weight="semibold" variant="secondary">
        Campaign history
      </Text>
      {rows.length === 0 ? (
        <Text size="xs" variant="tertiary">
          No campaign history
        </Text>
      ) : (
        <div className="max-h-[280px] overflow-y-auto">
          <Table dense>
            <TableHead>
              <TableRow>
                <TableHeader>Campaign</TableHeader>
                <TableHeader>Start date</TableHeader>
                <TableHeader>Status</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const badge = campaignStatusBadge(row.cm_status);
                return (
                  <TableRow
                    key={row.campaign_id}
                    className={row.campaign_id === selectedCampaignId ? 'bg-bg-secondary' : undefined}
                  >
                    <TableCell>{row.campaign_name ?? row.campaign_id}</TableCell>
                    <TableCell>
                      <span title={row.last_activity_at ?? undefined}>{row.start_date ?? '—'}</span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={badge.status}>{badge.label}</StatusBadge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
